import { AsyncSelect } from '@tempo-io/sui-select';
import { Spinner } from '@tempo-io/sui-spinner';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ControlProps,
  type GroupBase,
  type InputActionMeta,
  components as ReactSelectComponents,
} from 'react-select';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';
import { useMenuPortalBranch } from '@/common/hooks/useMenuPortalBranch';
import { MIN_SEARCH_LENGTH, type PickerOption, useItemPickerOptions } from './useItemPickerOptions';

interface ItemPickerProps {
  value: { id: number; name: string; itemTypeId: number } | null;
  onChange: (item: { id: number; name: string; itemTypeId: number } | null) => void;
  itemTypesById: ItemTypeMap;
  disabled?: boolean;
  placeholder?: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  filter?: ItemFiltersType;
  autoFocus?: boolean;
  isClearable?: boolean;
  excludedItemIds?: number[];
  eagerLoad?: boolean;
  leadingIcon?: ReactNode;
  withExternalKey?: boolean;
  persistInput?: boolean;
  loopId?: number;
  scenarioId?: number;
}

export const ItemPicker = ({
  value,
  onChange,
  itemTypesById,
  disabled,
  placeholder,
  onMenuOpen,
  onMenuClose,
  filter,
  autoFocus,
  isClearable = true,
  excludedItemIds,
  eagerLoad = false,
  leadingIcon,
  withExternalKey = false,
  persistInput = false,
  loopId,
  scenarioId,
}: ItemPickerProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const leadingIconComponents = useMemo(
    () =>
      leadingIcon
        ? {
            Control: (controlProps: ControlProps<PickerOption, false, GroupBase<PickerOption>>) => (
              <ReactSelectComponents.Control {...controlProps}>
                <span className="-ms-1 flex shrink-0 items-center pe-2 text-icon-neutral-secondary">
                  {leadingIcon}
                </span>
                {controlProps.children}
              </ReactSelectComponents.Control>
            ),
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            LoadingIndicator: () => <Spinner />,
          }
        : undefined,
    [leadingIcon],
  );

  const fetchItems = useItemPickerOptions({
    filter,
    excludedItemIds,
    eagerLoad,
    withExternalKey,
    loopId,
    scenarioId,
  });

  const formatOptionLabel = (option: PickerOption) => {
    const itemType = itemTypesById[option.itemTypeId];
    return (
      <div className="flex items-center gap-2">
        {!!itemType && <ItemTypeIcon itemType={itemType} className="size-4 shrink-0" />}
        <span className="truncate">{option.name}</span>
        {withExternalKey && !!option.externalKey && (
          <span className="ml-auto shrink-0 text-xs text-text-neutral-secondary">
            {option.externalKey}
          </span>
        )}
      </div>
    );
  };

  const pickerValue: PickerOption | null = value
    ? { id: value.id, name: value.name, itemTypeId: value.itemTypeId }
    : null;

  const {
    menuPortalTarget,
    menuPortalBranch,
    onMenuOpen: onPortalBranchMenuOpen,
    onMenuClose: onPortalBranchMenuClose,
  } = useMenuPortalBranch();

  return (
    <>
      {menuPortalBranch}
      <AsyncSelect<PickerOption>
        height={40}
        menuPosition="fixed"
        menuPortalTarget={menuPortalTarget}
        cacheOptions
        defaultOptions={eagerLoad}
        loadOptions={fetchItems}
        {...(leadingIconComponents ? { components: leadingIconComponents } : {})}
        {...(persistInput
          ? {
              inputValue,
              onInputChange: (next: string, meta: InputActionMeta) => {
                if (meta.action === 'input-change') {
                  setInputValue(next);
                } else if (meta.action === 'set-value') {
                  setInputValue('');
                }
              },
            }
          : {})}
        value={pickerValue}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.id.toString()}
        formatOptionLabel={formatOptionLabel}
        onChange={(selected) => {
          if (selected) {
            onChange({
              id: selected.id,
              name: selected.name,
              itemTypeId: selected.itemTypeId,
            });
          } else {
            onChange(null);
          }
        }}
        placeholder={
          placeholder || t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.placeholder)
        }
        isDisabled={disabled}
        isClearable={isClearable}
        onMenuOpen={() => {
          onPortalBranchMenuOpen();
          onMenuOpen?.();
        }}
        onMenuClose={() => {
          onPortalBranchMenuClose();
          onMenuClose?.();
        }}
        autoFocus={autoFocus}
        openMenuOnFocus={autoFocus && !value}
        noOptionsMessage={({ inputValue }) =>
          !eagerLoad && inputValue.trim().length < MIN_SEARCH_LENGTH
            ? t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.minCharacters, {
                count: MIN_SEARCH_LENGTH,
              })
            : t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.noResults)
        }
        data-testid="item-picker"
      />
    </>
  );
};
